"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Store, Loader2, ChevronDown, ChevronUp, Send, Info } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/components/ui/use-toast';
import { Link, useNavigate } from 'react-router-dom';

interface CatalogComment {
  id: string;
  product_catalog_id: string;
  user_id: string;
  store_id: string;
  order_id: string;
  content: string;
  created_at: string;
  parent_id?: string | null;
  profiles?: {
    username: string;
    avatar_url: string | null;
  };
  stores?: {
    name: string;
    slug: string;
  };
}

interface PurchaseInfo {
  can_comment: boolean;
  store_id: string;
  store_name: string;
  order_id: string;
}

interface ProductCatalogCommentsProps {
  productCatalogId: string;
  session: Session | null;
}

const ProductCatalogComments: React.FC<ProductCatalogCommentsProps> = ({ productCatalogId, session }) => {
  const navigate = useNavigate();
  const [comments, setComments] = useState<CatalogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [purchaseInfo, setPurchaseInfo] = useState<PurchaseInfo | null>(null);
  const [checkingPurchase, setCheckingPurchase] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmittedComment, setHasSubmittedComment] = useState(false); // Track if user just submitted
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const { toast } = useToast();

  const currentUserId = session?.user?.id;

  // Check if current user already commented as top-level post (not including replies)
  const userAlreadyCommented = hasSubmittedComment || (currentUserId && comments.some(c => c.user_id === currentUserId && !c.parent_id));

  // Sort top-level comments: current user's first, then date
  const topLevelComments = comments.filter(c => !c.parent_id);
  const replies = comments.filter(c => c.parent_id);

  const sortedTopLevel = [...topLevelComments].sort((a, b) => {
    if (currentUserId && a.user_id === currentUserId && b.user_id !== currentUserId) return -1;
    if (currentUserId && b.user_id === currentUserId && a.user_id !== currentUserId) return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Fetch comments for this catalog product
  const fetchComments = useCallback(async () => {
    if (!productCatalogId) return;
    
    console.debug('ProductCatalogComments: fetching for catalogId:', productCatalogId);
    
    try {
      const { data, error } = await supabase
        .from('product_catalog_comments')
        .select(`
          *,
          profiles:user_id (username, avatar_url),
          stores:store_id (name, slug)
        `)
        .eq('product_catalog_id', productCatalogId)
        .order('created_at', { ascending: false });

      console.debug('ProductCatalogComments: fetch result:', { data, error, count: data?.length });

      if (error) {
        console.warn('Error fetching catalog comments:', error);
        setComments([]);
      } else {
        setComments(data || []);
      }
    } catch (err) {
      console.error('Error fetching catalog comments:', err);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [productCatalogId]);

  // Check if current user can comment (has completed order)
  const checkCanComment = useCallback(async () => {
    if (!session?.user?.id || !productCatalogId) {
      console.debug('checkCanComment: missing session or catalogId', { userId: session?.user?.id, catalogId: productCatalogId });
      setPurchaseInfo(null);
      return;
    }

    setCheckingPurchase(true);
    try {
      console.debug('Starting purchase check for user:', session.user.id, 'catalogId:', productCatalogId);
      
      // Step 1: Get all orders for this user with completed/fulfilled status
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id, store_id, status, stores(name)')
        .eq('user_id', session.user.id)
        .in('status', ['completed', 'fulfilled'])
        .order('created_at', { ascending: false })
        .limit(10);

      console.debug('Orders query result:', { orders, ordersError });

      if (ordersError) {
        console.error('Orders query error:', ordersError);
        setPurchaseInfo(null);
        setCheckingPurchase(false);
        return;
      }

      if (!orders || orders.length === 0) {
        console.debug('User CANNOT comment - no orders found');
        setPurchaseInfo(null);
        setCheckingPurchase(false);
        return;
      }

      // Step 2: Get all order items for these orders
      const orderIds = orders.map(o => o.id);
      console.debug('Fetching order items for orderIds:', orderIds);
      
      const { data: items, error: itemsError } = await supabase
        .from('order_items')
        .select('order_id, product_id')
        .in('order_id', orderIds);

      console.debug('Order items query result:', { items, itemsError });

      if (itemsError || !items || items.length === 0) {
        console.debug('User CANNOT comment - no order items found');
        setPurchaseInfo(null);
        setCheckingPurchase(false);
        return;
      }

      // Step 3: Get all products for these items to check product_catalog_id
      const productIds = items.map(i => i.product_id);
      console.debug('Fetching products for productIds:', productIds);
      
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, product_catalog_id, details')
        .in('id', productIds);

      console.debug('Products query result:', { products, productsError });

      if (productsError || !products || products.length === 0) {
        console.debug('User CANNOT comment - no products found');
        setPurchaseInfo(null);
        setCheckingPurchase(false);
        return;
      }

      // Step 4: Check if any product matches our product_catalog_id
      let found = false;
      for (const product of products) {
        let catalogId = product.product_catalog_id;
        
        // Check if product_catalog_id is in details JSON
        if (!catalogId && product.details) {
          catalogId = (product.details as any)?.product_catalog_id;
        }
        
        console.debug('Checking product:', product.id, 'catalogId:', catalogId, 'looking for:', productCatalogId);
        
        if (catalogId === productCatalogId) {
          // Find the order and item that contains this product
          const item = items.find(i => i.product_id === product.id);
          if (item) {
            const order = orders.find(o => o.id === item.order_id);
            if (order) {
              found = true;
              setPurchaseInfo({
                can_comment: true,
                store_id: order.store_id,
                store_name: order.stores?.name || 'Tienda',
                order_id: order.id
              });
              console.debug('✓ User CAN comment, purchaseInfo set:', { 
                store_id: order.store_id, 
                store_name: order.stores?.name, 
                order_id: order.id 
              });
              break;
            }
          }
        }
      }

      if (!found) {
        console.debug('User CANNOT comment - no matching catalog product in orders');
        setPurchaseInfo(null);
      }
    } catch (err) {
      console.error('Error checking purchase status:', err);
      setPurchaseInfo(null);
    } finally {
      setCheckingPurchase(false);
    }
  }, [session?.user?.id, productCatalogId]);

  useEffect(() => {
    fetchComments();
    checkCanComment();
  }, [fetchComments, checkCanComment]);

  // Realtime subscription for new comments
  useEffect(() => {
    if (!productCatalogId) return;

    const channel = supabase
      .channel(`catalog_comments_${productCatalogId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_catalog_comments',
          filter: `product_catalog_id=eq.${productCatalogId}`
        },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [productCatalogId, fetchComments]);

  const handleSubmitComment = async (parentId: string | null = null, replyContent: string | null = null, setLocalReplyContent?: (val: string) => void) => {
    const isReply = !!parentId;
    const contentToSubmit = isReply ? replyContent?.trim() : newComment.trim();
    

    if (!session?.user?.id || !contentToSubmit || !purchaseInfo) {
      console.warn('ProductCatalogComments: Missing required data for submission');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('product_catalog_comments')
        .insert({
          product_catalog_id: productCatalogId,
          user_id: session.user.id,
          store_id: purchaseInfo.store_id,
          order_id: purchaseInfo.order_id,
          content: contentToSubmit,
          parent_id: parentId
        });

      if (error) {
        if (error.code === '23505' && !isReply) {
          toast({ title: 'Ya comentaste', description: 'Solo puedes dejar una opinión por producto', variant: 'destructive' });
          setHasSubmittedComment(true);
        } else {
          console.error('ProductCatalogComments error:', error);
          toast({ title: 'Error', description: 'No se pudo enviar el comentario', variant: 'destructive' });
        }
      } else {
        if (!isReply) {
          setNewComment('');
          setHasSubmittedComment(true);
        } else {
          setReplyToId(null);
          if (setLocalReplyContent) setLocalReplyContent('');
        }
        toast({ title: isReply ? 'Respuesta enviada' : 'Comentario enviado', description: isReply ? 'Tu respuesta ha sido publicada' : 'Tu opinión ha sido publicada' });
        fetchComments();
      }
    } catch (err) {
      console.error('ProductCatalogComments: catch error', err);
      toast({ title: 'Error', description: 'No se pudo enviar el comentario', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const CommentCard = ({ comment, level = 0 }: { comment: CatalogComment, level?: number }) => {
    const isOwnComment = comment.user_id === currentUserId;
    const childReplies = replies.filter(r => r.parent_id === comment.id);
    const [localReplyContent, setLocalReplyContent] = useState('');

    return (
      <div className="flex flex-col gap-2" style={{ marginLeft: `${Math.min(level * 1.5, 3)}rem` }}>
        <div 
          id={`comment-${comment.id}`} 
          className={cn(
            "flex gap-3 p-3 rounded-lg transition-all duration-300",
            isOwnComment ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/30'
          )}
        >
          <Link to={`/${comment.profiles?.username || ''}`}>
            <Avatar className="h-10 w-10">
              <AvatarImage src={comment.profiles?.avatar_url || undefined} />
              <AvatarFallback>
                {(comment.profiles?.username || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link 
                to={`/${comment.profiles?.username || ''}`}
                className="font-semibold text-sm hover:underline"
              >
                @{comment.profiles?.username || 'Usuario'}
              </Link>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: es })}
              </span>
              {!comment.parent_id && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <Link 
                    to={`/${comment.stores?.slug || ''}`}
                    className="text-xs text-primary flex items-center gap-1 hover:underline"
                  >
                    <Store className="h-3 v-3" />
                    {comment.stores?.name || 'Tienda'}
                  </Link>
                </>
              )}
              {isOwnComment && (
                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                  Tú
                </span>
              )}
            </div>
            <p className="text-sm mt-1 whitespace-pre-wrap break-words">
              {comment.content}
            </p>
            
            <div className="flex items-center gap-3 mt-2">
              <button 
                onClick={() => setReplyToId(replyToId === comment.id ? null : comment.id)}
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                disabled={!session || !purchaseInfo}
              >
                <MessageSquare className="h-3 w-3" />
                Responder
              </button>
            </div>
            
            {replyToId === comment.id && (
              <div className="mt-3 space-y-2 pb-2">
                <div className="text-[10px] text-muted-foreground mb-1">
                  Estás respondiendo a <span className="text-primary font-medium">@{comment.profiles?.username}</span>
                </div>
                <Textarea
                  placeholder={`Respondiendo a @${comment.profiles?.username}...`}
                  value={localReplyContent}
                  onChange={(e) => setLocalReplyContent(e.target.value)}
                  className="min-h-[60px] text-sm resize-none"
                  autoFocus
                />
                <div className="flex justify-end gap-3 mt-1">
                  <Button variant="ghost" size="sm" onClick={() => setReplyToId(null)}>
                    Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    disabled={!localReplyContent.trim() || submitting}
                    onClick={() => handleSubmitComment(comment.id, localReplyContent, setLocalReplyContent)}
                  >
                    {submitting && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    Responder
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {childReplies.length > 0 && (
          <div className="space-y-3 mt-1">
            {childReplies.map(reply => (
              <CommentCard key={reply.id} comment={reply} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mt-6 space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const displayComments = expanded ? sortedTopLevel : sortedTopLevel.slice(0, 3);
  const hasMoreComments = sortedTopLevel.length > 3;

  return (
    <div className="mt-6 border-t border-primary/30 pt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Opiniones de compradores verificados
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground ml-1 flex items-center">
                  <Info className="h-4 w-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Solo los usuarios que compraron este producto en el pasado pueden dejar un comentario.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {comments.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({comments.length})
            </span>
          )}
        </h3>
      </div>

      {/* Comment form for verified purchasers (only if hasn't commented yet) */}
      {session && !userAlreadyCommented && (
        <div className="mb-4">
          {checkingPurchase ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando compra...
            </div>
          ) : purchaseInfo ? (
            <div className="space-y-3 p-3 bg-secondary/20 rounded-lg">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Store className="h-4 w-4" />
                Compraste en <span className="font-semibold">{purchaseInfo.store_name}</span>
              </p>
              <Textarea
                placeholder="Comparte tu experiencia con este producto..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[80px] resize-none"
                maxLength={1000}
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || submitting}
                  size="sm"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Publicar
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic flex items-center gap-2">
              Solo los compradores verificados pueden dejar opiniones sobre este producto.
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground">
                      <Info className="h-4 w-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Solo los usuarios que compraron este producto en el pasado pueden dejar un comentario.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </p>
          )}
        </div>
      )}

      {/* Login prompt for unauthenticated users */}
      {!session && (
        <div className="mb-4">
          <div className="p-3 bg-secondary/20 rounded-lg border border-primary/30">
            <p className="text-sm text-muted-foreground mb-3">
              Inicia sesión para compartir tu opinión sobre este producto
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Ingresa para comentar
            </Button>
          </div>
        </div>
      )}
      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Aún no hay opiniones para este producto.
        </p>
      ) : (
        <div className="space-y-6">
          {displayComments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} />
          ))}

          {/* Show more/less button */}
          {hasMoreComments && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-2"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Mostrar menos
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Ver {sortedTopLevel.length - 3} opiniones más
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCatalogComments;
